import { Box, PopperPlacementType, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';

import {
  getPresentationHoverOutlineSx,
  usePresentationRowHover,
} from './PresentationHoverActions.hooks';
import { PresentationRowHoverToolbar } from './PresentationRowHoverToolbar';

interface PresentationHoverRowProps {
  /** Stable id for this row, used to track which single row is hovered. */
  id: string;
  /** Row content. */
  children: ReactNode;
  /**
   * The floating toolbar contents (action buttons), shown only while the
   * row's hover treatment is active. Omit to get just the blue outline.
   */
  toolbar?: ReactNode;
  /** Suppress the hover treatment (e.g. while a launched modal is open). */
  disabled?: boolean;
  /** Toolbar placement relative to the row. Defaults to top-left. */
  toolbarPlacement?: PopperPlacementType;
  /** Extra styles merged onto the wrapper. */
  sx?: SxProps<Theme>;
}

/**
 * Convenience wrapper that applies the presentation blue-hover treatment
 * (outline + floating toolbar) to a block of content. Outside a
 * `PresentationHoverActionsProvider` it renders its children with no
 * decoration. For DataGrid rows — where an extra wrapping element would
 * fight the grid layout — use `usePresentationRowHover` directly on the
 * row element instead.
 */
export function PresentationHoverRow({
  id,
  children,
  toolbar,
  disabled,
  toolbarPlacement,
  sx,
}: PresentationHoverRowProps) {
  const {
    isOutlineActive,
    isToolbarActive,
    anchorRef,
    rowProps,
    toolbarProps,
  } = usePresentationRowHover(id, { disabled });

  return (
    <Box
      ref={anchorRef}
      {...rowProps}
      sx={[
        getPresentationHoverOutlineSx(isOutlineActive),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
      {isToolbarActive && toolbar && anchorRef.current && (
        <PresentationRowHoverToolbar
          anchorEl={anchorRef.current}
          placement={toolbarPlacement}
          onMouseEnter={toolbarProps.onMouseEnter}
          onMouseLeave={toolbarProps.onMouseLeave}
        >
          {toolbar}
        </PresentationRowHoverToolbar>
      )}
    </Box>
  );
}
